import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { log } from "./log";
import { api } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=guest, obj=auth
  const [ready, setReady] = useState(false);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth (session_id fragment present),
    // skip /auth/me — AuthCallback will exchange the session_id first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setReady(false);
      return;
    }
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    // The backend sets httpOnly access_token + refresh_token cookies.
    // We deliberately do NOT persist the JWT anywhere on the client — cookies
    // are the single source of truth.
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const completeGoogleSession = async (sessionId) => {
    const { data } = await api.post("/auth/google/session", null, {
      headers: { "X-Session-ID": sessionId },
    });
    setUser(data);
    setReady(true);
    return data;
  };

  const register = async (email, password, name) => {
    // Backend sets httpOnly cookies + returns the user record — we auto-login the
    // client. No email verification step.
    const { data } = await api.post("/auth/register", { email, password, name });
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) {
      // Non-blocking: clear local state even if the server call fails
      if (process.env.NODE_ENV !== "production") {
        log.warn("[auth] Logout request failed", err);
      }
    }
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, ready, login, loginWithGoogle, completeGoogleSession, register, logout, refresh: fetchMe }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
